import { useSearchParams } from 'react-router-dom';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Card, CardContent, Typography, TextField, MenuItem, Grid2 as Grid, Chip,
  Button, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  CircularProgress, Alert, AlertTitle, Dialog, DialogTitle, DialogContent,
  DialogActions, IconButton, Stack, Divider, Tooltip, Switch, FormControlLabel,
  Autocomplete,
} from '@mui/material';
import {
  Gavel as ContratoIcon, Refresh as RefreshIcon, Add as AddIcon,
  Delete as DeleteIcon, Calculate as SimularIcon, Warning as AvisoIcon,
  Search as PesquisaIcon,
} from '@mui/icons-material';
import {
  contratosService, Contrato, TaxaCarregamento, Instrumento, Portador,
  ConjuntoClausulas, bpsParaPercento, percentoParaBps, comissaoDoBilhete, emVigor,
  Servico, DefServico, defDesconhecida,
} from '../../services/contratos.service';
import entitiesService, { Entity } from '../../services/entities.service';
import { nowCv, todayCv } from '../../utils/date';
import ContratosEmFalta from './ContratosEmFalta';

const cve = (n: number) => `${n.toLocaleString('pt-PT')}$`;
const data = (s: string | null) => (s ? new Date(s).toLocaleDateString('pt-PT', { timeZone: 'UTC' }) : '—');

const ESTADO_COR: Record<string, 'success' | 'default' | 'warning' | 'error'> = {
  ACTIVE: 'success', DRAFT: 'warning', SUPERSEDED: 'default', TERMINATED: 'error',
};

const INSTRUMENTOS: Instrumento[] = ['VINTI4', 'VISA', 'MASTERCARD', 'CASH', 'BANK_TRANSFER'];

const DIAS_SEMANA = ['domingo', 'segunda-feira', 'terça-feira', 'quarta-feira',
  'quinta-feira', 'sexta-feira', 'sábado'];

/** Preços com que vale a pena simular: um bilhete normal e um VIP, onde o tecto pega. */
const PRECOS_SIMULACAO = [500, 2500, 25000];

interface Opcao { id: string; nome: string; codigo: string }

/**
 * Sem acentos, sem maiúsculas, sem pontuação.
 *
 * Em Cabo Verde metade dos nomes tem acento — «Água Brava», «A Nação», «Odjanu
 * Ótica». Procurar «agua» tem de encontrar «Água», ou o campo de pesquisa é
 * decorativo. NFD separa a letra do acento; o \p{Diacritic} remove o acento.
 */
const semAcentos = (s: string): string =>
  s.normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase().trim();

/**
 * Pesquisa por TERMOS, não por prefixo.
 *
 * «sos aldeias» encontra «Aldeias Infantis SOS»: cada palavra escrita tem de
 * aparecer em algum sítio do nome ou do código, em qualquer ordem. Exigir a ordem
 * certa obriga a saber como o nome está escrito, que é precisamente o que não se
 * sabe quando se procura numa lista de 71.
 *
 * A ordenação premeia quem começa pelo termo: escrever «ad» põe a ADEI e a ADS
 * antes de «Sociedade de Ádegas».
 */
function filtrarPromotores(opcoes: Opcao[], escrito: string): Opcao[] {
  const termos = semAcentos(escrito).split(/\s+/).filter(Boolean);
  if (!termos.length) return opcoes;

  const pontuado = opcoes
    .map((o) => {
      const nome = semAcentos(o.nome);
      const codigo = semAcentos(o.codigo);
      const alvo = `${nome} ${codigo}`;
      if (!termos.every((t) => alvo.includes(t))) return null;

      // Quanto mais cedo o primeiro termo aparecer, mais relevante. Começar pelo
      // termo vale mais do que tê-lo no meio; o código exacto vale mais que tudo.
      let pontos = 0;
      if (codigo === termos.join('')) pontos += 100;
      if (nome.startsWith(termos[0])) pontos += 50;
      if (nome.split(/\s+/).some((p) => p.startsWith(termos[0]))) pontos += 20;
      pontos -= Math.min(nome.indexOf(termos[0]), 19);
      return { o, pontos };
    })
    .filter((x): x is { o: Opcao; pontos: number } => x !== null);

  return pontuado
    .sort((a, b) => b.pontos - a.pontos || a.o.nome.localeCompare(b.o.nome, 'pt'))
    .map((x) => x.o);
}

export default function ContratosPage() {
  // Vindo de Entidades → Serviços: ?entidade=…&servico=… abre já o contrato certo.
  const [params] = useSearchParams();
  const [entidades, setEntidades] = useState<Opcao[]>([]);
  const [promotor, setPromotor] = useState(params.get('entidade') ?? '');
  // Cada entidade tem um contrato por serviço — eventos e vouchers vivem lado a lado.
  const [servico, setServico] = useState<Servico>(params.get('servico') ?? 'EVENTOS');
  const [servicos, setServicos] = useState<DefServico[]>([]);
  const [emFalta, setEmFalta] = useState(false);
  const defDe = useCallback(
    (c: Servico) => servicos.find((x) => x.codigo === c) ?? defDesconhecida(c), [servicos]);

  useEffect(() => {
    contratosService.servicos()
      .then(setServicos)
      .catch((e: any) => setErro(`Não foi possível listar os serviços: ${e.message}`));
  }, []);
  const [versoes, setVersoes] = useState<Contrato[]>([]);
  const [detalhe, setDetalhe] = useState<Contrato | null>(null);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [formAberto, setFormAberto] = useState(false);
  // O rascunho a corrigir. Null = o formulario cria uma versao nova, como antes.
  const [editar, setEditar] = useState<Contrato | null>(null);
  const [aMudar, setAMudar] = useState<string | null>(null);

  useEffect(() => {
    entitiesService.getEntities({ limit: 200 })
      .then((r) => {
        // `desig` e o nome real. A interface prometia `name`, que a API nunca devolveu,
        // e o resultado era uma lista de itens sem texto.
        setEntidades((r.data ?? [])
          .map((e: Entity) => ({
            id: e.id,
            nome: e.desig || e.nomeContacto || e.codigo || e.id,
            codigo: e.codigo ?? '',
          }))
          .sort((a, b) => a.nome.localeCompare(b.nome, 'pt')));
      })
      .catch((e: any) => setErro(`Não foi possível listar entidades: ${e.message}`));
  }, []);

  const carregar = useCallback(async (entidadeId: string, serv: Servico) => {
    if (!entidadeId) return;
    setCarregando(true); setErro(null); setDetalhe(null);
    try {
      const lista = await contratosService.listar(entidadeId, serv);
      setVersoes(lista);
      if (lista.length) {
        setDetalhe(await contratosService.detalhe(lista[0].id, lista[0].status, serv));
      }
    } catch (e: any) {
      setErro(e.message);
      setVersoes([]);
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => { if (promotor) carregar(promotor, servico); }, [promotor, servico, carregar]);

  // Activar uma versao SUBSTITUI as outras activas do mesmo promotor — e o backend
  // recusa se, depois da operacao, ficasse mais do que uma em vigor. Aqui so se
  // reporta o que ele fez.
  const mudarEstado = useCallback(async (v: Contrato, status: 'ACTIVE' | 'TERMINATED') => {
    const aviso = status === 'ACTIVE'
      ? `Pôr a v${v.version} em vigor a partir de ${new Date(v.effectiveFrom).toLocaleDateString('pt-PT', { timeZone: 'UTC' })}?`
        + ` Qualquer outra versão activa de ${defDe(v.servico).nome} desta entidade passa a SUPERSEDED.`
      : `Terminar a v${v.version}? Esta entidade fica sem contrato de ${defDe(v.servico).nome} em vigor,`
        + ' e as vendas deixam de conseguir resolver a comissão.';
    if (!window.confirm(aviso)) return;
    setAMudar(v.id); setErro(null);
    try {
      await contratosService.mudarEstado(v.id, status);
      await carregar(v.promoterEntityId, v.servico);
    } catch (e: any) {
      setErro(e.message);
    } finally {
      setAMudar(null);
    }
  }, [carregar, defDe]);


  const vigenteHoje = useMemo(
    () => versoes.find((v) => emVigor(v)) ?? null, [versoes],
  );

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <ContratoIcon color="primary" />
        <Typography variant="h5" fontWeight={600}>Contratos</Typography>
        <Box flex={1} />
        <IconButton onClick={() => carregar(promotor, servico)} disabled={!promotor}><RefreshIcon /></IconButton>
        <Button variant="outlined" onClick={() => setEmFalta(true)}>Contratos em falta</Button>
        <Button variant="contained" startIcon={<AddIcon />} disabled={!promotor}
          onClick={() => setFormAberto(true)}>Nova versão</Button>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        As condições comerciais de cada serviço contratado — a comissão de cada bilhete,
        de cada voucher vendido, de cada carregamento. Cada serviço tem o seu contrato.
        Uma nova versão nunca reescreve a anterior — a que estava em vigor na data de uma
        venda tem de continuar legível para o extracto se poder justificar.
      </Typography>

      {erro && <Alert severity="error" sx={{ mb: 2 }}>{erro}</Alert>}

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 3 }} alignItems="flex-start">
      <Autocomplete
        options={entidades}
        value={entidades.find((e) => e.id === promotor) ?? null}
        onChange={(_, v) => setPromotor(v?.id ?? '')}
        getOptionLabel={(o) => o.nome}
        isOptionEqualToValue={(a, b) => a.id === b.id}
        filterOptions={(opcoes, estado) => filtrarPromotores(opcoes, estado.inputValue)}
        // 71 entidades num dropdown obrigam a percorrer a lista a olho; a partir de
        // 8 resultados mostra-se o que cabe e o resto filtra-se a escrever.
        noOptionsText="Nenhuma entidade corresponde"
        sx={{ minWidth: 460 }}
        size="small"
        renderOption={(props, o) => (
          <Box component="li" {...props} key={o.id}>
            <Box>
              <Typography variant="body2">{o.nome}</Typography>
              {o.codigo && (
                <Typography variant="caption" color="text.secondary">{o.codigo}</Typography>
              )}
            </Box>
          </Box>
        )}
        renderInput={(params) => (
          <TextField {...params} label="Entidade" placeholder="Escrever nome ou código…"
            helperText={`${entidades.length} entidades — ignora acentos e aceita palavras em qualquer ordem`}
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <>
                  <PesquisaIcon fontSize="small" sx={{ ml: 0.5, mr: 0.5, color: 'text.disabled' }} />
                  {params.InputProps.startAdornment}
                </>
              ),
            }} />
        )}
      />
      <TextField select size="small" label="Serviço" value={servico} sx={{ minWidth: 240 }}
        onChange={(e) => setServico(e.target.value as Servico)}>
        {servicos.map((sv) => <MenuItem key={sv.codigo} value={sv.codigo}>{sv.nome}</MenuItem>)}
        {!servicos.length && <MenuItem value="EVENTOS">Eventos (bilheteira)</MenuItem>}
      </TextField>
      </Stack>

      {carregando && <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>}

      {!carregando && promotor && !versoes.length && (
        <Alert severity="info">
          Esta entidade não tem contrato de {defDe(servico).nome}. Crie a primeira versão.
        </Alert>
      )}

      {!carregando && !!versoes.length && !vigenteHoje && (
        <Alert severity="warning" sx={{ mb: 2 }} icon={<AvisoIcon />}>
          <AlertTitle>Nenhuma versão em vigor hoje</AlertTitle>
          Há {versoes.length} versão(ões), mas nenhuma vigora à data de hoje. Uma venda
          feita agora não consegue resolver a comissão — o estado ACTIVE não basta, a
          vigência é uma janela entre datas.
        </Alert>
      )}

      {!!versoes.length && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Versões</Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Versão</TableCell>
                    <TableCell>Estado</TableCell>
                    <TableCell>Em vigor de</TableCell>
                    <TableCell>até</TableCell>
                    <TableCell>Escalões</TableCell>
                    <TableCell align="center">Hoje</TableCell>
                    <TableCell align="right">Acção</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {versoes.map((v) => (
                    <TableRow key={v.id} hover selected={detalhe?.id === v.id}
                      sx={{ cursor: 'pointer' }}
                      onClick={async () => {
                        try { setDetalhe(await contratosService.detalhe(v.id, v.status, v.servico)); }
                        catch (e: any) { setErro(e.message); }
                      }}>
                      <TableCell>v{v.version}</TableCell>
                      <TableCell><Chip size="small" label={v.status} color={ESTADO_COR[v.status] ?? 'default'} /></TableCell>
                      <TableCell>{data(v.effectiveFrom)}</TableCell>
                      <TableCell>{v.effectiveTo ? data(v.effectiveTo) : 'sem fim'}</TableCell>
                      <TableCell>{v.tierCount ?? '—'}</TableCell>
                      <TableCell align="center">
                        {emVigor(v)
                          ? <Chip size="small" color="success" label="em vigor" />
                          : <Tooltip title={new Date(v.effectiveFrom) > nowCv()
                              ? 'A data de início ainda não chegou' : 'Fora da janela de vigência ou substituída'}>
                              <Chip size="small" variant="outlined" label="não" />
                            </Tooltip>}
                      </TableCell>
                      <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                        {/* Gravar uma versão cria-a em DRAFT e não muda nada no que se
                            cobra. Sem esta acção o ecrã deixava o trabalho a meio: era
                            possível preparar a versão e impossível pô-la em vigor. */}
                        {v.status === 'DRAFT' && (
                          <>
                            {/* Corrigir antes de por em vigor. Sem isto, mudar uma
                                percentagem obrigava a apagar o rascunho e recria-lo. */}
                            <Button size="small" sx={{ mr: 1 }}
                              onClick={async () => {
                                try {
                                  // O detalhe e preciso: a linha da lista so traz o
                                  // resumo, e abrir o formulario com ele perdia os
                                  // escaloes e as taxas de carregamento.
                                  setEditar(await contratosService.detalhe(v.id, v.status, v.servico));
                                  setFormAberto(true);
                                } catch (e: any) { setErro(e.message); }
                              }} disabled={!!aMudar}>
                              Corrigir
                            </Button>
                            <Button size="small" variant="outlined"
                              onClick={() => mudarEstado(v, 'ACTIVE')} disabled={!!aMudar}>
                              Pôr em vigor
                            </Button>
                          </>
                        )}
                        {v.status === 'ACTIVE' && (
                          <Tooltip title="Termina esta versão. Fica sem contrato em vigor até activar outra.">
                            <Button size="small" color="warning"
                              onClick={() => mudarEstado(v, 'TERMINATED')} disabled={!!aMudar}>
                              Terminar
                            </Button>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {detalhe && <Detalhe c={detalhe} sv={defDe(detalhe.servico)} />}

      {emFalta && (
        <ContratosEmFalta onFechar={(criou) => { setEmFalta(false); if (criou && promotor) carregar(promotor, servico); }} />
      )}
      {formAberto && (
        <FormNovaVersao
          promotor={promotor}
          servico={servico}
          sv={defDe(servico)}
          base={editar ?? detalhe}
          editar={editar}
          onFechar={() => { setFormAberto(false); setEditar(null); }}
          onGravado={() => { setFormAberto(false); setEditar(null); carregar(promotor, servico); }}
        />
      )}
    </Box>
  );
}

const DIAS_ISO = ['', 'segunda', 'terça', 'quarta', 'quinta', 'sexta', 'sábado', 'domingo'];
const textoLiquidacao = (p: string, d: number | null) =>
  p === 'DIARIA' ? 'diária' : p === 'SEMANAL' ? `semanal, à ${DIAS_ISO[d ?? 1] ?? '?'}` : p === 'MENSAL' ? `mensal, dia ${d ?? 1}` : p;

function Detalhe({ c, sv }: { c: Contrato; sv: DefServico }) {
  // Bilheteira, convites, cashless e carregamentos só existem nos eventos. Nos outros
  // serviços o contrato é a taxa por venda (com os escalões) e o IVA.
  const eEventos = c.servico === 'EVENTOS';
  return (
    <Grid container spacing={2}>
      <Grid size={{ xs: 12, md: 7 }}>
        <Card>
          <CardContent>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              {eEventos ? 'Bilheteira' : `${sv.nome} — comissão por venda`} — v{c.version}
            </Typography>
            {sv.remuneracao === 'SISP' && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Serviço remunerado pela comissão da SISP — a entidade não paga comissão à
                Pagali salvo excepção neste contrato.
              </Alert>
            )}
            <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Escalão</TableCell>
                    <TableCell>Até ({sv.unidade})</TableCell>
                    <TableCell align="right">Taxa</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {c.escaloes.length === 0 && (
                    <TableRow><TableCell colSpan={3}>
                      <Typography variant="body2" color="text.secondary">Sem escalões definidos.</Typography>
                    </TableCell></TableRow>
                  )}
                  {c.escaloes.map((e) => (
                    <TableRow key={e.tierIndex}>
                      <TableCell>{e.tierIndex + 1}º</TableCell>
                      <TableCell>{e.maxTickets === null ? 'sem limite' : e.maxTickets.toLocaleString('pt-PT')}</TableCell>
                      <TableCell align="right">{bpsParaPercento(e.rateBps)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
              {eEventos && <>
                <Campo rot="Tecto por bilhete" val={c.perTicketCapCVE == null ? 'sem tecto' : cve(c.perTicketCapCVE)} />
                <Campo rot="Convites: taxa fixa"
                  val={c.freeTicketFeeCVE > 0
                    ? `${cve(c.freeTicketFeeCVE)} ${c.freeTicketFeeOn === 'CHECKIN' ? 'ao check-in' : 'à emissão'}`
                    : 'sem custo'} />
                <Campo rot="Convites: percentagem" val={c.freeTicketsExempt ? 'isentos' : 'com comissão'} />
              </>}
              <Campo rot="Comissão sobre" val={c.comissaoBase === 'SUBTOTAL' ? 'o valor sem a entrega' : 'todo o valor pago'} />
              {c.taxaFixaCVE > 0 && <Campo rot="Taxa fixa por transacção" val={cve(c.taxaFixaCVE)} />}
              {c.taxaEntregaCVE != null && <Campo rot="Taxa de entrega ao cliente" val={cve(c.taxaEntregaCVE)} />}
              {c.entregaModo && <Campo rot="Modo da taxa de entrega"
                val={c.entregaModo === 'FIXO' ? 'fixa (excepção)' : `por distância (excepção)${c.entregaBaseCVE != null ? ` · base ${cve(c.entregaBaseCVE)}` : ''}${c.entregaKmIncluidos != null ? ` · ${c.entregaKmIncluidos} km incluídos` : ''}${c.entregaPrecoKmCVE != null ? ` · ${cve(c.entregaPrecoKmCVE)}/km` : ''}`} />}
              {c.valorEstafetaCVE != null && <Campo rot="Estafeta recebe por entrega" val={cve(c.valorEstafetaCVE)} />}
              {c.liquidacaoPeriodicidade && <Campo rot="Liquidação" val={textoLiquidacao(c.liquidacaoPeriodicidade, c.liquidacaoDia)} />}
              <Campo rot="IVA" val={bpsParaPercento(c.vatRateBps)} />
              <Campo rot="Taxa SISP"
                val={c.sispFeeMode === 'PASS_THROUGH'
                  ? `cobrada à parte — Vinti4 ${c.sispRateVinti4Bps == null ? 'valor real' : bpsParaPercento(c.sispRateVinti4Bps)}, ` +
                    `Visa/MC/Amex ${c.sispRateIntlBps == null ? 'valor real' : bpsParaPercento(c.sispRateIntlBps)}` +
                    `${c.sispFeeVat ? ' + IVA sobre a margem' : ''}`
                  : 'incluída na comissão'} />
              <Campo rot="Ano de contrato começa" val={c.contractYearStartMD} />
              {eEventos && <>
                <Campo rot="Transferências" val={`todas as ${DIAS_SEMANA[c.settlementDow] ?? '?'}s`} />
                <Campo rot="Cashless liquidado" val={`${c.settlementCashlessDias} dias após o evento`} />
              </>}
              {/*
                Qual o TEXTO que este promotor lê. Sem isto, a única forma de saber se
                ele está no padrão ou numa excepção era ir à base de dados.
              */}
              {c.clausulas && (
                <Campo rot="Cláusulas"
                  val={c.clausulas.total === 0
                    ? 'sem texto associado'
                    : `${c.clausulas.ePadrao ? 'padrão' : 'EXCEPÇÃO'} v${c.clausulas.version}`
                      + ` · ${c.clausulas.total} cláusulas`
                      + (c.clausulas.origem === 'PADRAO' ? ' (não fixado nesta versão)' : '')} />
              )}
            </Stack>

            {eEventos && <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Cashless</Typography>
            <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
              <Campo rot="Consumo" val={bpsParaPercento(c.consumptionRateBps)} />
              <Campo rot="Transferência" val={bpsParaPercento(c.transferRateBps)} />
            </Stack>

            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Carregamento</Typography>
            <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
              <Campo rot="Suportado por" val={c.topupBearer === 'PROMOTER' ? 'promotor' : 'participante'} />
              <Campo rot="Taxa por omissão" val={bpsParaPercento(c.topupDefaultRateBps)} />
              <Campo rot="Mínimo" val={c.topupMinCVE == null ? '—' : cve(c.topupMinCVE)} />
              <Campo rot="Máximo" val={c.topupMaxCVE == null ? '—' : cve(c.topupMaxCVE)} />
            </Stack>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {c.taxasCarregamento.map((t) => (
                <Chip key={t.instrument} size="small" variant="outlined"
                  label={`${t.instrument}: ${bpsParaPercento(t.rateBps)}`} />
              ))}
              {!c.taxasCarregamento.length && (
                <Typography variant="body2" color="text.secondary">
                  Sem taxas por instrumento — aplica-se a taxa por omissão.
                </Typography>
              )}
            </Stack>
            </>}
          </CardContent>
        </Card>
      </Grid>

      <Grid size={{ xs: 12, md: 5 }}>
        <Card>
          <CardContent>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
              <SimularIcon fontSize="small" color="action" />
              <Typography variant="subtitle2">Efeito real</Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Um tecto não se percebe numa tabela de percentagens. Percebe-se aqui.
            </Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{eEventos ? 'Bilhete' : 'Venda'}</TableCell>
                    <TableCell align="right">Taxa</TableCell>
                    <TableCell align="right">Comissão</TableCell>
                    <TableCell align="right">{eEventos ? 'Ao promotor' : 'À empresa'}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {PRECOS_SIMULACAO.map((p) => {
                    const r = comissaoDoBilhete(p, c);
                    return (
                      <TableRow key={p}>
                        <TableCell>{cve(p)}</TableCell>
                        <TableCell align="right">{bpsParaPercento(r.rateBps)}</TableCell>
                        <TableCell align="right">
                          {cve(r.comissaoCVE)}
                          {r.tectoPegou && (
                            <Tooltip title={`Sem tecto seriam ${cve(r.brutaCVE)}`}>
                              <Chip size="small" color="warning" label="tecto" sx={{ ml: 0.5 }} />
                            </Tooltip>
                          )}
                        </TableCell>
                        <TableCell align="right">{cve(p - r.comissaoCVE)}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
}

function Campo({ rot, val }: { rot: string; val: string }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" display="block">{rot}</Typography>
      <Typography variant="body2" fontWeight={500}>{val}</Typography>
    </Box>
  );
}

function FormNovaVersao({ promotor, servico, sv, base, editar, onFechar, onGravado }: {
  promotor: string; servico: Servico; sv: DefServico; base: Contrato | null; editar?: Contrato | null;
  onFechar: () => void; onGravado: () => void;
}) {
  const eEventos = servico === 'EVENTOS';
  // Parte-se da versão actual quando existe: uma nova versão costuma mudar uma linha,
  // não tudo, e obrigar a reescrever o resto convida a erros de transcrição.
  // A corrigir um rascunho, a data de inicio e a DELE. Pre-encher com hoje mudava em
  // silencio a vigencia de quem so queria acertar uma percentagem.
  const [efeitoEm, setEfeitoEm] = useState(
    editar ? String(editar.effectiveFrom).slice(0, 10) : todayCv());
  const [anoMD, setAnoMD] = useState(base?.contractYearStartMD ?? '01-01');
  const [tecto, setTecto] = useState(base?.perTicketCapCVE != null ? String(base.perTicketCapCVE) : '');
  const [gratisIsentos, setGratisIsentos] = useState(base?.freeTicketsExempt ?? true);
  const [taxaConvite, setTaxaConvite] = useState(base?.freeTicketFeeCVE ? String(base.freeTicketFeeCVE) : '');
  const [quandoConvite, setQuandoConvite] = useState<'CHECKIN' | 'ISSUE'>(base?.freeTicketFeeOn ?? 'CHECKIN');
  const [iva, setIva] = useState(base ? String(base.vatRateBps / 100).replace('.', ',') : '15');
  const [consumo, setConsumo] = useState(base ? String(base.consumptionRateBps / 100).replace('.', ',') : '0');
  const [transf, setTransf] = useState(base ? String(base.transferRateBps / 100).replace('.', ',') : '0');
  const [portador, setPortador] = useState<Portador>(base?.topupBearer ?? 'PARTICIPANT');
  const [taxaOmissao, setTaxaOmissao] = useState(base ? String(base.topupDefaultRateBps / 100).replace('.', ',') : '0');
  const [minimo, setMinimo] = useState(base?.topupMinCVE != null ? String(base.topupMinCVE) : '');
  const [maximo, setMaximo] = useState(base?.topupMaxCVE != null ? String(base.topupMaxCVE) : '');
  const [dowLiq, setDowLiq] = useState(base?.settlementDow ?? 1);
  const [diasCashless, setDiasCashless] = useState(String(base?.settlementCashlessDias ?? 5));
  const [escaloes, setEscaloes] = useState<Array<{ max: string; taxa: string }>>(
    base?.escaloes.length
      ? base.escaloes.map((e) => ({ max: e.maxTickets == null ? '' : String(e.maxTickets), taxa: String(e.rateBps / 100).replace('.', ',') }))
      // A taxa por omissão do serviço: 10% nos de venda, 0 nos pagos pela SISP.
      : [{ max: '', taxa: String(sv.omissaoBps / 100).replace('.', ',') }],
  );
  const [taxas, setTaxas] = useState<TaxaCarregamento[]>(base?.taxasCarregamento ?? []);
  const [sispFeeMode, setSispFeeMode] = useState<'INCLUDED' | 'PASS_THROUGH'>(base?.sispFeeMode ?? 'INCLUDED');
  // Campos genéricos (qualquer serviço). Sem versão anterior, a omissão do serviço.
  const om = sv.contratoOmissao ?? {};
  const eFood = servico === 'FOOD';
  const [comissaoBase, setComissaoBase] = useState<'BRUTO' | 'SUBTOTAL'>(base?.comissaoBase ?? om.comissao_base ?? 'BRUTO');
  const [taxaFixa, setTaxaFixa] = useState(String(base?.taxaFixaCVE ?? om.taxa_fixa_cve ?? 0));
  const [taxaEntrega, setTaxaEntrega] = useState(String(base?.taxaEntregaCVE ?? om.taxa_entrega_cve ?? ''));
  const [valorEstafeta, setValorEstafeta] = useState(String(base?.valorEstafetaCVE ?? om.valor_estafeta_cve ?? ''));
  // Taxa de entrega: '' = tabela global (Admin › Pagali Food › Taxa de entrega); FIXO; DISTANCIA
  const [entregaModo, setEntregaModo] = useState<string>(base?.entregaModo ?? '');
  const [entregaBase, setEntregaBase] = useState(base?.entregaBaseCVE != null ? String(base.entregaBaseCVE) : '');
  const [entregaKm, setEntregaKm] = useState(base?.entregaKmIncluidos != null ? String(base.entregaKmIncluidos) : '');
  const [entregaPrecoKm, setEntregaPrecoKm] = useState(base?.entregaPrecoKmCVE != null ? String(base.entregaPrecoKmCVE) : '');
  const [periodicidade, setPeriodicidade] = useState<string>(base?.liquidacaoPeriodicidade ?? om.liquidacao_periodicidade ?? '');
  const [diaLiq, setDiaLiq] = useState<number>(base?.liquidacaoDia ?? om.liquidacao_dia ?? 1);
  const [sispFeeVat, setSispFeeVat] = useState<boolean>(base?.sispFeeVat ?? false);
  // Taxas cobradas ao promotor, em %. Vazio = repassa o valor real da SISP.
  const bpsTxt = (b: number | null | undefined) => (b == null ? '' : String(b / 100).replace('.', ','));
  const [sispVinti4, setSispVinti4] = useState<string>(bpsTxt(base?.sispRateVinti4Bps));
  const [sispIntl, setSispIntl] = useState<string>(bpsTxt(base?.sispRateIntlBps));
  // Um texto que não é número não pode virar «valor real» em silêncio (NaN vai como null).
  const taxaSisp = (txt: string, nome: string): number | null => {
    if (sispFeeMode !== 'PASS_THROUGH' || txt.trim() === '') return null;
    const b = percentoParaBps(txt);
    if (!Number.isFinite(b) || b < 0 || b > 10000) throw new Error(`Taxa SISP ${nome} inválida: «${txt}»`);
    return b;
  };
  // Vazio = usar o padrão em vigor. É o caso normal: quase todos os promotores usam o
  // mesmo texto, e só quem negociou algo diferente aponta para uma excepção.
  const [conjunto, setConjunto] = useState<string>('');
  const [conjuntos, setConjuntos] = useState<ConjuntoClausulas[]>([]);
  const [gravando, setGravando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    contratosService.conjuntosDeClausulas(servico)
      .then(setConjuntos)
      // A falta da lista não impede gravar: sem escolha, o backend usa o padrão, que é
      // o que se queria em quase todos os casos.
      .catch(() => setConjuntos([]));
  }, [servico]);

  const problemas = useMemo(() => {
    const p: string[] = [];
    if (!efeitoEm) p.push('A data de início é obrigatória.');
    if (!/^\d{2}-\d{2}$/.test(anoMD)) p.push('O início do ano de contrato deve ser MM-DD.');
    if (!escaloes.length) p.push('É preciso pelo menos um escalão.');
    escaloes.forEach((e, i) => {
      if (!Number.isFinite(percentoParaBps(e.taxa))) p.push(`Escalão ${i + 1}: taxa inválida.`);
      if (i < escaloes.length - 1 && !e.max) p.push(`Escalão ${i + 1}: só o último pode ser sem limite.`);
    });
    // Os limites têm de ser crescentes, senão um escalão nunca é alcançável.
    const limites = escaloes.slice(0, -1).map((e) => Number(e.max));
    for (let i = 1; i < limites.length; i++) {
      if (limites[i] <= limites[i - 1]) p.push(`Escalão ${i + 1}: o limite tem de ser maior que o anterior.`);
    }
    if (!Number.isFinite(percentoParaBps(iva))) p.push('IVA inválido.');
    if (minimo && maximo && Number(minimo) > Number(maximo)) p.push('O mínimo de carregamento não pode exceder o máximo.');
    if (eFood && taxaEntrega !== '' && valorEstafeta !== '' && Number(valorEstafeta) > Number(taxaEntrega)) {
      p.push('O estafeta não pode receber mais do que a taxa de entrega cobrada ao cliente.');
    }
    return p;
  }, [efeitoEm, anoMD, escaloes, iva, minimo, maximo, eFood, taxaEntrega, valorEstafeta]);

  const gravar = async () => {
    setGravando(true); setErro(null);
    try {
      const dados = {
        promoterEntityId: promotor,
        servico,
        effectiveFrom: efeitoEm,
        contractYearStartMD: anoMD,
        perTicketCapCVE: tecto === '' ? null : Number(tecto),
        freeTicketsExempt: gratisIsentos,
        ticketFreeFeeCVE: taxaConvite === '' ? 0 : Number(taxaConvite),
        ticketFreeFeeOn: quandoConvite,
        vatRateBps: percentoParaBps(iva),
        consumptionRateBps: percentoParaBps(consumo),
        transferRateBps: percentoParaBps(transf),
        topupBearer: portador,
        topupDefaultRateBps: percentoParaBps(taxaOmissao),
        topupMinCVE: minimo === '' ? null : Number(minimo),
        topupMaxCVE: maximo === '' ? null : Number(maximo),
        settlementTicketingDow: dowLiq,
        settlementCashlessDays: Number(diasCashless) || 5,
        tiers: escaloes.map((e, i) => ({
          tierIndex: i,
          maxTickets: e.max === '' ? null : Number(e.max),
          rateBps: percentoParaBps(e.taxa),
        })),
        topupRates: taxas.map((t) => ({ instrument: t.instrument, rateBps: t.rateBps })),
        clausulasTemplateId: conjunto === '' ? null : conjunto,
        sispFeeMode,
        sispFeeVat,
        sispRateVinti4Bps: taxaSisp(sispVinti4, 'Vinti4'),
        sispRateIntlBps: taxaSisp(sispIntl, 'Visa/Mastercard/Amex'),
        comissaoBase,
        taxaFixaCVE: Number(taxaFixa) || 0,
        taxaEntregaCVE: taxaEntrega === '' ? null : Number(taxaEntrega),
        valorEstafetaCVE: valorEstafeta === '' ? null : Number(valorEstafeta),
        ...(eFood ? {
          entregaModo: (entregaModo || null) as any,
          entregaBaseCVE: entregaBase === '' ? null : Number(entregaBase),
          entregaKmIncluidos: entregaKm === '' ? null : Number(entregaKm),
          entregaPrecoKmCVE: entregaPrecoKm === '' ? null : Number(entregaPrecoKm),
        } : {}),
        liquidacaoPeriodicidade: (periodicidade || null) as any,
        liquidacaoDia: periodicidade ? diaLiq : null,
      };
      if (editar) await contratosService.editarRascunho(editar.id, dados);
      else await contratosService.criarVersao(dados);
      onGravado();
    } catch (e: any) {
      setErro(e.message);
    } finally {
      setGravando(false);
    }
  };

  return (
    <Dialog open onClose={onFechar} maxWidth="md" fullWidth>
      <DialogTitle>
        {editar ? `Corrigir o rascunho v${editar.version}` : 'Nova versão do contrato'} — {sv.nome}
      </DialogTitle>
      <DialogContent dividers>
        {erro && <Alert severity="error" sx={{ mb: 2 }}>{erro}</Alert>}
        {sv.remuneracao === 'SISP' && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Em {sv.nome} a Pagali é remunerada pela <b>comissão da SISP</b>: a entidade não
            paga comissão à Pagali e a taxa é 0. Pôr aqui uma taxa é uma excepção negociada.
          </Alert>
        )}
        {!!problemas.length && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <AlertTitle>Corrigir antes de gravar</AlertTitle>
            {problemas.map((p) => <div key={p}>{p}</div>)}
          </Alert>
        )}

        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField fullWidth size="small" type="date" label="Em vigor a partir de"
              value={efeitoEm} onChange={(e) => setEfeitoEm(e.target.value)}
              InputLabelProps={{ shrink: true }}
              helperText="A comissão de uma venda usa a versão em vigor NA DATA DA VENDA." />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField fullWidth size="small" label="Ano de contrato começa (MM-DD)"
              value={anoMD} onChange={(e) => setAnoMD(e.target.value)} />
          </Grid>
          {eEventos && (
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField fullWidth size="small" label="Tecto por bilhete (CVE)" value={tecto}
              onChange={(e) => setTecto(e.target.value.replace(/[^0-9]/g, ''))}
              helperText="Vazio = sem tecto" />
          </Grid>
          )}
          {/*
            O TEXTO do contrato. Os números estão neste ecrã, o texto escreve-se no
            super-admin do events.cv — e é aqui que se liga um ao outro.

            Deixar no padrão é o caso normal. Só se escolhe uma excepção quando este
            promotor negociou cláusulas diferentes; se ela ainda não existir, escreve-se
            primeiro lá e depois aponta-se aqui.
          */}
          <Grid size={{ xs: 12 }}>
            <TextField select fullWidth size="small" label="Cláusulas do contrato"
              value={conjunto} onChange={(e) => setConjunto(e.target.value)}
              helperText={
                conjunto === ''
                  ? 'Usa o padrão em vigor, e fica fixado nesta versão: corrigir o padrão '
                    + 'mais tarde não muda o texto deste contrato.'
                  : 'Excepção — este promotor passa a ver texto próprio, diferente do padrão.'
              }>
              <MenuItem value="">Padrão em vigor (recomendado)</MenuItem>
              {conjuntos.filter((c) => !c.ePadrao).map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  Excepção v{c.version} — {c.clausulas} cláusulas
                  {c.notas ? ` · ${c.notas}` : ''}
                  {c.contratos > 0 ? ` · em ${c.contratos} contrato(s)` : ''}
                </MenuItem>
              ))}
            </TextField>
            {conjuntos.filter((c) => !c.ePadrao).length === 0 && (
              <Typography variant="caption" color="text.secondary">
                Não há excepções publicadas. Para criar texto próprio para um promotor:
                events.cv → super-admin → Contratos → «Excepção».
              </Typography>
            )}
          </Grid>
        </Grid>

        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          {eEventos ? 'Escalões de bilheteira' : `Comissão sobre cada venda (escalões por ${sv.unidade} vendidos)`}
        </Typography>
        <Stack spacing={1} sx={{ mb: 2 }}>
          {escaloes.map((e, i) => (
            <Stack key={i} direction="row" spacing={1} alignItems="center">
              <Typography variant="body2" sx={{ minWidth: 28 }}>{i + 1}º</Typography>
              <TextField size="small" label={`Até (${sv.unidade})`} value={e.max}
                onChange={(ev) => {
                  const v = ev.target.value.replace(/[^0-9]/g, '');
                  setEscaloes(escaloes.map((x, j) => (j === i ? { ...x, max: v } : x)));
                }}
                placeholder={i === escaloes.length - 1 ? 'sem limite' : ''} sx={{ width: 170 }} />
              <TextField size="small" label="Taxa (%)" value={e.taxa}
                onChange={(ev) => setEscaloes(escaloes.map((x, j) => (j === i ? { ...x, taxa: ev.target.value } : x)))}
                sx={{ width: 130 }} />
              <IconButton size="small" disabled={escaloes.length === 1}
                onClick={() => setEscaloes(escaloes.filter((_, j) => j !== i))}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Stack>
          ))}
          <Button size="small" startIcon={<AddIcon />}
            onClick={() => setEscaloes([...escaloes, { max: '', taxa: '' }])}>
            Acrescentar escalão
          </Button>
        </Stack>

        {/* Campos genéricos: valem para QUALQUER serviço (decisão da Linda, 22/10/2026) */}
        <Grid container spacing={2} sx={{ mb: 1 }}>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField select fullWidth size="small" label="A comissão incide sobre" value={comissaoBase}
              onChange={(e) => setComissaoBase(e.target.value as any)}
              helperText={eFood ? 'Pagali Food: pratos + taxa de entrega, ou só os pratos' : 'O que o cliente pagou'}>
              <MenuItem value="BRUTO">Todo o valor pago</MenuItem>
              <MenuItem value="SUBTOTAL">O valor sem a taxa de entrega</MenuItem>
            </TextField>
          </Grid>
          <Grid size={{ xs: 6, sm: 4 }}>
            <TextField fullWidth size="small" label="Taxa fixa por transacção (CVE)" value={taxaFixa}
              onChange={(e) => setTaxaFixa(e.target.value.replace(/[^0-9.]/g, ''))}
              helperText="Soma-se à percentagem. 0 = sem taxa fixa" />
          </Grid>
          <Grid size={{ xs: 6, sm: 4 }}>
            <TextField select fullWidth size="small" label="Liquidação" value={periodicidade}
              onChange={(e) => setPeriodicidade(e.target.value)}
              helperText={eEventos ? 'Vazio = a regra dos eventos (abaixo)' : 'Quando a entidade recebe'}>
              <MenuItem value="">{eEventos ? 'Regra dos eventos' : 'A definir'}</MenuItem>
              <MenuItem value="DIARIA">Diária</MenuItem>
              <MenuItem value="SEMANAL">Semanal</MenuItem>
              <MenuItem value="MENSAL">Mensal</MenuItem>
            </TextField>
          </Grid>
          {periodicidade === 'SEMANAL' && (
            <Grid size={{ xs: 6, sm: 4 }}>
              <TextField select fullWidth size="small" label="Dia da semana" value={diaLiq}
                onChange={(e) => setDiaLiq(Number(e.target.value))}>
                {[1, 2, 3, 4, 5, 6, 7].map((d) => <MenuItem key={d} value={d}>{DIAS_ISO[d]}</MenuItem>)}
              </TextField>
            </Grid>
          )}
          {periodicidade === 'MENSAL' && (
            <Grid size={{ xs: 6, sm: 4 }}>
              <TextField fullWidth size="small" label="Dia do mês" value={diaLiq}
                onChange={(e) => setDiaLiq(Math.min(28, Math.max(1, Number(e.target.value.replace(/[^0-9]/g, '')) || 1)))}
                helperText="1 a 28" />
            </Grid>
          )}
          {eFood && <>
            <Grid size={{ xs: 6, sm: 4 }}>
              <TextField fullWidth size="small" label="Taxa de entrega ao cliente (CVE)" value={taxaEntrega}
                onChange={(e) => setTaxaEntrega(e.target.value.replace(/[^0-9]/g, ''))}
                helperText="Cobrada pela Pagali quando entrega o estafeta Pagali" />
            </Grid>
            <Grid size={{ xs: 6, sm: 4 }}>
              <TextField fullWidth size="small" label="Estafeta recebe por entrega (CVE)" value={valorEstafeta}
                onChange={(e) => setValorEstafeta(e.target.value.replace(/[^0-9]/g, ''))}
                helperText="A diferença cobre a SISP da entrega. Liquidado semanalmente" />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField fullWidth size="small" select label="Taxa de entrega" value={entregaModo}
                onChange={(e) => setEntregaModo(e.target.value)}
                helperText="Excepção à tabela global (Pagali Food › Taxa de entrega)">
                <MenuItem value="">Tabela global</MenuItem>
                <MenuItem value="FIXO">Fixa (o valor acima)</MenuItem>
                <MenuItem value="DISTANCIA">Por distância</MenuItem>
              </TextField>
            </Grid>
            {entregaModo !== 'FIXO' && <>
              <Grid size={{ xs: 4, sm: 4 }}>
                <TextField fullWidth size="small" label="Base (CVE)" value={entregaBase} placeholder="global"
                  onChange={(e) => setEntregaBase(e.target.value.replace(/[^0-9]/g, ''))}
                  helperText="Vazio = valor global" />
              </Grid>
              <Grid size={{ xs: 4, sm: 4 }}>
                <TextField fullWidth size="small" label="Km incluídos" value={entregaKm} placeholder="global"
                  onChange={(e) => setEntregaKm(e.target.value.replace(/[^0-9.]/g, ''))}
                  helperText="Vazio = valor global" />
              </Grid>
              <Grid size={{ xs: 4, sm: 4 }}>
                <TextField fullWidth size="small" label="Preço por km (CVE)" value={entregaPrecoKm} placeholder="global"
                  onChange={(e) => setEntregaPrecoKm(e.target.value.replace(/[^0-9]/g, ''))}
                  helperText="Vazio = valor global" />
              </Grid>
            </>}
          </>}
        </Grid>

        <Divider sx={{ my: 2 }} />
        <Grid container spacing={2}>
          <Grid size={{ xs: 6, sm: 3 }}>
            <TextField fullWidth size="small" label="IVA (%)" value={iva} onChange={(e) => setIva(e.target.value)} />
          </Grid>
          {/*
            A comissão que a SISP cobra pelos pagamentos com cartão. «Incluída» é o que todos
            os contratos fizeram até 19/10/2026. «À parte» desconta ao promotor, em cada
            liquidação, o valor REAL que a SISP cobrou (Reconciliação SISP) — a Pagali
            entrega-o à SISP.
          */}
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField select fullWidth size="small" label="Taxa SISP" value={sispFeeMode}
              onChange={(e) => setSispFeeMode(e.target.value as any)}
              helperText={sispFeeMode === 'PASS_THROUGH'
                ? 'Descontada na liquidação, sobre o cartão real de cada transacção'
                : 'Já está dentro da comissão da Pagali'}>
              <MenuItem value="INCLUDED">Incluída na comissão</MenuItem>
              <MenuItem value="PASS_THROUGH">Cobrada à parte (repasse do valor real)</MenuItem>
            </TextField>
          </Grid>
          {sispFeeMode === 'PASS_THROUGH' && (
            <Grid size={{ xs: 12, sm: 3 }}>
              <FormControlLabel control={
                <Switch checked={sispFeeVat} onChange={(e) => setSispFeeVat(e.target.checked)} />
              } label="IVA sobre a margem (nunca sobre o custo SISP)" />
            </Grid>
          )}
          {/*
            O que se cobra ao promotor pode não ser o que a SISP cobra à Pagali (margem).
            Contrato SISP (MD050.02, 2026): Vinti4 0,70% + IVA; internacionais 2,5% sem IVA. Vazio = repasse
            do valor real; a liquidação guarda sempre o custo real ao lado.
          */}
          {sispFeeMode === 'PASS_THROUGH' && <>
            <Grid size={{ xs: 6, sm: 3 }}>
              <TextField fullWidth size="small" label="Vinti4 (%)" value={sispVinti4}
                onChange={(e) => setSispVinti4(e.target.value)} placeholder="valor real"
                helperText="SISP: 0,70% + IVA (= 0,805%)" />
            </Grid>
            <Grid size={{ xs: 6, sm: 3 }}>
              <TextField fullWidth size="small" label="Visa/Mastercard/Amex (%)" value={sispIntl}
                onChange={(e) => setSispIntl(e.target.value)} placeholder="valor real"
                helperText="SISP: 2,5%, sem IVA" />
            </Grid>
          </>}
          {eEventos && <>
          <Grid size={{ xs: 6, sm: 3 }}>
            <TextField fullWidth size="small" label="Consumo (%)" value={consumo} onChange={(e) => setConsumo(e.target.value)} />
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <TextField fullWidth size="small" label="Transferência (%)" value={transf} onChange={(e) => setTransf(e.target.value)} />
          </Grid>
          </>}
        </Grid>

        {eEventos && <>
        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle2" sx={{ mb: 1 }}>Convites (bilhetes a 0 CVE)</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Um convite está sempre isento da comissão percentual, porque qualquer taxa
          sobre zero dá zero. O que se define aqui é a taxa FIXA que o promotor paga
          por cada um — coisa diferente, e que faltava ao modelo.
        </Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField fullWidth size="small" label="Taxa por convite (CVE)" value={taxaConvite}
              onChange={(e) => setTaxaConvite(e.target.value.replace(/[^0-9]/g, ''))}
              helperText="Vazio ou 0 = convites sem custo" />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField select fullWidth size="small" label="Cobrada quando"
              value={quandoConvite} onChange={(e) => setQuandoConvite(e.target.value as any)}
              disabled={!taxaConvite || taxaConvite === '0'}
              helperText={quandoConvite === 'CHECKIN'
                ? 'Só os convites usados. Apura-se depois do evento.'
                : 'Todos os emitidos, usados ou não. Sabe-se na véspera.'}>
              <MenuItem value="CHECKIN">Ao validar no check-in</MenuItem>
              <MenuItem value="ISSUE">Ao emitir o convite</MenuItem>
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <FormControlLabel control={
              <Switch checked={gratisIsentos} onChange={(e) => setGratisIsentos(e.target.checked)} />
            } label="Convites isentos da percentagem" />
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle2" sx={{ mb: 1 }}>Carregamento</Typography>
        <Grid container spacing={2} sx={{ mb: 1 }}>
          <Grid size={{ xs: 6, sm: 3 }}>
            <TextField select fullWidth size="small" label="Suportado por" value={portador}
              onChange={(e) => setPortador(e.target.value as Portador)}>
              <MenuItem value="PARTICIPANT">Participante</MenuItem>
              <MenuItem value="PROMOTER">Promotor</MenuItem>
            </TextField>
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <TextField fullWidth size="small" label="Taxa por omissão (%)" value={taxaOmissao}
              onChange={(e) => setTaxaOmissao(e.target.value)} />
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <TextField fullWidth size="small" label="Mínimo (CVE)" value={minimo}
              onChange={(e) => setMinimo(e.target.value.replace(/[^0-9]/g, ''))} />
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <TextField fullWidth size="small" label="Máximo (CVE)" value={maximo}
              onChange={(e) => setMaximo(e.target.value.replace(/[^0-9]/g, ''))} />
          </Grid>
        </Grid>
        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle2" sx={{ mb: 1 }}>Quando o promotor recebe</Typography>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid size={{ xs: 12, sm: 5 }}>
            <TextField select fullWidth size="small" label="Transferência de bilheteira"
              value={dowLiq} onChange={(e) => setDowLiq(Number(e.target.value))}
              helperText="Semanal, no dia escolhido">
              {DIAS_SEMANA.map((d, i) => <MenuItem key={i} value={i}>Todas as {d}s</MenuItem>)}
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField fullWidth size="small" label="Cashless: dias após o evento"
              value={diasCashless}
              onChange={(e) => setDiasCashless(e.target.value.replace(/[^0-9]/g, ''))}
              helperText="O saldo das pulseiras só se apura depois de fechar" />
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle2" sx={{ mb: 1 }}>Taxas por instrumento</Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {INSTRUMENTOS.map((inst) => {
            const actual = taxas.find((t) => t.instrument === inst);
            return (
              <TextField key={inst} size="small" label={inst} sx={{ width: 130 }}
                value={actual ? String(actual.rateBps / 100).replace('.', ',') : ''}
                placeholder="omissão"
                onChange={(e) => {
                  const bps = percentoParaBps(e.target.value);
                  const resto = taxas.filter((t) => t.instrument !== inst);
                  setTaxas(e.target.value === '' || !Number.isFinite(bps)
                    ? resto : [...resto, { instrument: inst, rateBps: bps }]);
                }} />
            );
          })}
        </Stack>
        </>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onFechar} disabled={gravando}>Cancelar</Button>
        <Button variant="contained" onClick={gravar} disabled={gravando || !!problemas.length}>
          {gravando ? 'A gravar…' : 'Criar versão'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
